// import useApiFetch from "@hooks/useApiFetch";
import MoveArrowSVG from "@components/svgs/drag-arrows.svg";
import RightArrowSVG from "@components/svgs/right-arrow.svg";
import XIconSVG from "@components/svgs/x-icon.svg";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import useAnalytics from "@hooks/useAnalytics";
import useClientContext, { setModal } from "@hooks/useClientContext";
import useGame, { getDriver, IDriver } from "@hooks/useGame";
import useGigya from "@hooks/useGigya";
import usePicksContext, { Slot, updatePicks } from "@hooks/usePicksContext";
import useSession from "@hooks/useSession";
import _cloneDeep from "lodash.clonedeep";
import React from "react";
import { hexRGBA, media, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
import { ButtonBase } from "./Button";
import { TCurrentView } from "./RoundColumn";

interface Props {
  id: string;
  slotNumber: number;
  driverId: IDriver["driver_id"] | null;
  currentView: TCurrentView;
  isEditable: boolean;
  isPickable: boolean;
  points?: number;
  roundId: number;
  isCollapsed: boolean;
  hasScoring: boolean;
}

function DriverSlot({
  id,
  driverId,
  slotNumber,
  isPickable,
  currentView,
  isEditable,
  points,
  roundId,
  isCollapsed,
  hasScoring,
}: Props) {
  const { data: game } = useGame();
  const { data: session } = useSession();
  const [{ currentEntry }, clientDispatch] = useClientContext();
  const [{ picks }, picksDispatch] = usePicksContext();
  const { showAuthModal } = useGigya();
  const { trackEvent } = useAnalytics();

  const driver = getDriver(driverId, game);
  const isFilled = Boolean(driverId);
  const isLoggedIn = Boolean(session.user.token);
  const current_round = game?.state?.current_round;
  const selectedIndex =
    (driverId && picks?.[roundId + 1]?.findIndex((slot) => slot.pick === driverId)) ?? -1;
  const isSelected = selectedIndex >= 0;
  const shouldShowEditControls = !isPickable && isFilled && isEditable;
  const odds = driver?.odds[roundId];
  const oddsText = odds && `${odds}`.length < 6 ? `${odds > 0 ? `+${odds}` : odds}` : ` `;

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "none",
  };

  /**
   * Get the next round's first open slot index (if available)
   */
  function getFirstOpenSlotIndex(): number | null {
    // This if statement checks if Current Round has been determined from Game State, if not we don't want to continue and cause an error.
    if (current_round === undefined) return;
    const openSlot =
      picks[current_round]?.findIndex(
        (slot: Slot) => slot.pick === undefined || slot.pick === null,
      ) ?? -1;
    return openSlot < 0 ? null : openSlot;
  }

  const nextOpenSlotIndex = getFirstOpenSlotIndex();
  const nextOpenSlotLabel = nextOpenSlotIndex === null ? null : nextOpenSlotIndex + 1;

  function makePick(driverId: number, slotNumber: number): void {
    if (!isLoggedIn) {
      return showAuthModal();
    }

    if (!currentEntry) {
      return clientDispatch(setModal("CREATE_ENTRY"));
    }

    if (driverId && nextOpenSlotIndex === null) return;

    const newPicks = _cloneDeep(picks);

    if (driverId === null) {
      newPicks[current_round][slotNumber - 1].pick = driverId;
    } else {
      newPicks[current_round][nextOpenSlotIndex].pick = driverId;
    }

    picksDispatch(updatePicks(newPicks));
    trackEvent("picks:make_pick");
  }

  const pickedDriverId = picks[roundId]?.[slotNumber - 1].pick;
  const pickedDriverName =
    game?.drivers?.find((driver) => driver.driver_id === pickedDriverId)?.name ?? "No Driver";
  const hasScoringElements =
    currentView === "scoring" && hasScoring && driverId && pickedDriverId !== driverId;

  const currentRound = game?.rounds?.find((r) => r.round_id === game.state?.current_round) ?? null;
  const now = Date.now();
  const currentRndIsPostLock = now > currentRound?.locks_at ?? false;
  const currentRndHasScoring = Boolean(
    currentRndIsPostLock && currentRound.scoring_status === "final",
  );

  const shouldShowOdds =
    (isPickable && currentView === "picking") ||
    (currentView === "picking" &&
      roundId === current_round - 1 &&
      currentRndIsPostLock &&
      !currentRndHasScoring);

  return (
    <SlotContainer
      isPickable={isPickable}
      isSelected={isSelected}
      isCollapsed={isCollapsed}
      isFilled={isFilled}
      ref={setNodeRef}
      style={style}
      {...attributes}
      tabIndex={-1}
    >
      <CarNumber
        isFilled={isFilled}
        onClick={() => makePick(driverId, slotNumber)}
        disabled={!isCollapsed || !isPickable || isSelected || nextOpenSlotLabel === null}
      >
        {isFilled
          ? driver && (
              <img width="24" height="24" src={driver?.badge} alt={`Car #${driver?.car_number}`} />
            )
          : slotNumber}
      </CarNumber>
      <DriverName
        disabled={!isFilled}
        isFilled={isFilled}
        onClick={() => clientDispatch(setModal("DRIVER_MODAL", { driverId }))}
        hasScoringElements={hasScoringElements}
      >
        {hasScoringElements && (
          <PickedDriverName>
            you picked <span className={points === 0 ? "incorrect" : ""}>{pickedDriverName}</span>
          </PickedDriverName>
        )}
        {driver?.name}
      </DriverName>
      <PointsIndicator isFilled={isFilled} currentView={currentView} pointsValue={points}>
        {shouldShowEditControls ? (
          <PickButton
            onClick={() => makePick(null, slotNumber)}
            aria-label={`Remove pick ${driver?.full_name} from slot ${slotNumber}`}
          >
            <XIconSVG width="12" height="12" />
          </PickButton>
        ) : (
          <>
            {isPickable && isFilled && (
              <>
                {isSelected ? (
                  <PickButton
                    onClick={() => makePick(null, selectedIndex + 1)}
                    aria-label={`Remove pick ${driver?.full_name} from slot ${selectedIndex + 1}`}
                  >
                    <XIconSVG width="12" height="12" />
                  </PickButton>
                ) : (
                  <PickButton
                    onClick={() => makePick(driverId, slotNumber)}
                    disabled={isSelected || nextOpenSlotLabel === null}
                    aria-label={`Add pick ${driver?.full_name} to slot ${nextOpenSlotLabel}`}
                  >
                    <RightArrowSVG width="12" height="12" />
                  </PickButton>
                )}
              </>
            )}
            <PointSpan>{points}</PointSpan>
          </>
        )}
      </PointsIndicator>
      <OddsArea
        {...listeners}
        shouldShowEditControls={shouldShowEditControls}
        disabled={!shouldShowEditControls}
      >
        {shouldShowOdds ? oddsText : <MoveArrowSVG width="12" height="12" />}
      </OddsArea>
    </SlotContainer>
  );
}

export default DriverSlot;

const CarNumber = styled.button<{ isFilled: boolean }>`
  ${ButtonBase}
  flex: 0 0 ${pxToRem(34)};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  background-color: ${({ isFilled }) => (isFilled ? "#fff" : "transparent")};
  border: ${({ isFilled, theme }) => !isFilled && `${pxToRem(1)} solid ${theme.grey_200}`};
  color: ${({ theme }) => theme.grey_150};
  font-size: ${pxToRem(14)};
  font-weight: bold;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  &:disabled {
    opacity: unset;
    cursor: default;
  }
`;

const DriverName = styled.button<{ isFilled: boolean; hasScoringElements: boolean }>`
  ${ButtonBase}
  flex: 1 0 ${pxToRem(150)};
  margin: 0 ${pxToRem(4)};
  font-size: ${({ hasScoringElements }) => (hasScoringElements ? pxToRem(12) : pxToRem(14))};
  background-color: ${({ isFilled, theme }) => (isFilled ? "#fff" : theme.grey_200)};
  color: black;
  font-family: ${({ theme }) => theme.fontPrimary};
  font-weight: bold;
  line-height: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-left: ${pxToRem(4)};
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  max-height: ${pxToRem(32)};

  &:disabled {
    cursor: default;
    opacity: 1;
  }
`;
const PickedDriverName = styled.div`
  font-size: ${pxToRem(10)};
  font-family: ${({ theme }) => theme.fontPrimary};
  font-style: italic;
  color: ${hexRGBA("#000", 0.6)};
  text-transform: none;
  line-height: ${pxToRem(6)};
  margin-bottom: ${pxToRem(5)};

  span {
    text-transform: uppercase;

    &.incorrect {
      text-decoration: line-through 1px;
    }
  }
`;

const PointSpan = styled.span`
  font-size: ${pxToRem(14)};
  font-weight: bold;
  color: white;
  text-shadow: ${pxToRem(1)} ${pxToRem(1)} ${pxToRem(1.5)} rgba(0, 0, 0, 0.9);
`;

interface PointsProps {
  isFilled: boolean;
  currentView: TCurrentView;
  pointsValue?: number;
}

const PointsIndicator = styled.div<PointsProps>`
  flex: 0 0 ${pxToRem(19)};
  color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;

  background-color: ${({ pointsValue, theme, isFilled, currentView }) =>
    isFilled && currentView === "picking"
      ? "#fff"
      : !isFilled && currentView === "picking"
      ? theme.grey_200
      : pointsValue === 10
      ? theme.grey_150
      : pointsValue === 15
      ? theme.green
      : pointsValue === 11
      ? theme.gold
      : "transparent"};

  svg {
    transition: transform 0.35s ease-in-out;
    display: ${({ currentView }) => currentView === "scoring" && "none"};
  }

  ${PointSpan} {
    display: ${({ currentView }) => currentView === "picking" && "none"};
  }
`;
const PickButton = styled.button`
  ${ButtonBase}
  padding: 0;
  height: 100%;
  width: auto;
  color: inherit;
  &:focus,
  :focus-within {
    outline: none;
  }
`;

const OddsArea = styled.button<{ shouldShowEditControls: boolean }>`
  ${ButtonBase}
  padding: 0;
  flex: 0 1 ${pxToRem(30)};
  margin-left: ${pxToRem(3)};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: ${({ theme }) => theme.grey_100};
  font-size: ${pxToRem(9)};
  font-style: italic;
  text-align: left;
  pointer-events: ${({ shouldShowEditControls }) => !shouldShowEditControls && "none"};

  cursor: ${({ shouldShowEditControls }) => shouldShowEditControls && "grab"} !important;
  &:active {
    cursor: ${({ shouldShowEditControls }) => shouldShowEditControls && "grabbing"} !important;
  }

  svg {
    color: white;
    visibility: ${({ shouldShowEditControls }) => !shouldShowEditControls && "hidden"};
  }
`;
interface SlotContainerProps {
  isPickable: boolean;
  isSelected: boolean;
  isCollapsed: boolean;
  isFilled: boolean;
}
const SlotContainer = styled.div<SlotContainerProps>`
  display: flex;
  min-width: ${pxToRem(235)};
  flex: 0 0 ${pxToRem(32)};
  user-select: none;

  &:not(:last-child) {
    margin-bottom: 6px;
  }

  &:hover {
    ${PointsIndicator} {
      background: ${({ isPickable, isSelected, isFilled }) =>
        isFilled && isPickable && !isSelected && `transparent`};
      color: ${({ theme, isPickable }) => isPickable && theme.gold};

      svg {
        transform: ${({ isPickable, isSelected }) =>
          isPickable && !isSelected && `translateX(3px)`};
        display: ${({ isPickable, isSelected }) =>
          isSelected ? "none" : isPickable && !isSelected && `block`};
      }
      ${media.tablet} {
        color: ${({ isSelected }) => isSelected && "black"};
        svg {
          display: ${({ isPickable }) => isPickable && `block`};
        }
      }
      ${PointSpan} {
        display: ${({ isPickable }) => isPickable && `none`};
      }
    }
  }

  ${PointsIndicator} {
    button {
      pointer-events: ${({ isSelected }) => isSelected && `none`};
    }
    svg {
      display: ${({ isSelected }) => isSelected && `none`};
    }

    ${media.tablet} {
      button {
        pointer-events: ${({ isSelected, isPickable }) => isSelected && isPickable && `all`};
      }
      svg {
        display: ${({ isSelected, isPickable }) => isSelected && isPickable && `block`};
      }
    }
  }

  > *:not(:last-child) {
    /* background-color: ${({ isSelected }) => isSelected && hexRGBA("#fff", 0.8)}; */
    opacity: ${({ isSelected }) => isSelected && 0.8};
  }

  ${media.tablet} {
    min-width: unset;
    > *:not(:first-child) {
      display: ${({ isCollapsed }) => isCollapsed && "none"};
    }

    ${CarNumber} {
      outline: ${({ isSelected, isCollapsed, theme }) =>
        isSelected && isCollapsed && `2px solid ${theme.gold}`};
      outline-offset: ${pxToRem(2)};
    }
  }
`;
